"use client";

/* ─── Reusable sub-components ──────────────────────────────────────────────── */

function SectionHeading({ n, title }: { n: string; title: string }) {
  return (
    <h2 className="mt-10 mb-4 text-[0.95rem] font-bold tracking-wide text-[#0D1B3E] uppercase">
      {n}. {title}
    </h2>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-md border-l-[3px] border-[#C95A0E] bg-[#FDF8F4] px-4 py-3">
      <p className="m-0 text-[0.72rem] leading-relaxed text-[#5A3E2B] italic">
        {children}
      </p>
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export default function PrivacyPolicyPage() {
  return (
    <main className="bg-white font-['DM_Sans',sans-serif] text-[#333]">
      {/* ── Hero ── */}
      <section className="bg-[#0D1B3E] py-16 text-center text-white">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[0.6rem] font-bold uppercase tracking-[0.25em] text-[#C95A0E]">
            Legal
          </p>
          <h1 className="mt-2 font-['Cormorant_Garamond',Georgia,serif] text-[1.75rem] font-semibold leading-tight sm:text-[2.25rem]">
            Privacy Policy
          </h1>
          <p className="mt-2 text-[0.72rem] text-white/50">
            Farsight Solutions LLP &nbsp;|&nbsp; Effective: December 30, 2024
          </p>
        </div>
      </section>

      {/* ── Key badges ── */}
      <div className="mx-auto -mt-6 grid max-w-3xl grid-cols-1 gap-3 px-6 sm:grid-cols-3">
        {[
          { label: "Jurisdiction", value: "India (DPDP Act 2023 + IT Act 2000)" },
          { label: "Data Sale", value: "We NEVER sell your personal data" },
          { label: "Your Rights", value: "Access, Correct, Delete, Opt-Out" },
        ].map((b) => (
          <div
            key={b.label}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center shadow-sm"
          >
            <p className="text-[0.6rem] font-bold uppercase tracking-[0.15em] text-[#C95A0E]">
              {b.label}
            </p>
            <p className="mt-1 text-[0.7rem] font-medium leading-snug text-[#0D1B3E]">
              {b.value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Body ── */}
      <article className="prose-none mx-auto max-w-3xl px-6 pb-20 pt-10 text-[0.75rem] leading-[1.85] text-[#444]">
        {/* 1 ─ Introduction */}
        <SectionHeading n="1" title="Introduction" />
        <p>
          Welcome to Farsight Solutions LLP. We are a multi-service consultancy and digital platform headquartered in India, providing startup consultancy, financial advisory, digital marketing, and tech consulting to startups, entrepreneurs, SMEs, and professionals across India.
        </p>
        <p>
          At Farsight Solutions LLP, we understand that your personal and business information is valuable. This Privacy Policy explains, in plain language, what data we collect, why we collect it, how we use it, who we share it with, and the rights you have over your information.
        </p>
        <p>
          This Privacy Policy applies to all users of our website (www.farsightsolutions.in), mobile applications, consultation booking portals, lead generation forms, WhatsApp and telephonic communication channels, email marketing, and any future AI-powered services or platforms operated by Farsight Solutions LLP.
        </p>
        <p>
          By accessing our website, submitting any form, communicating with us, or using any of our services, you acknowledge that you have read, understood, and agreed to the terms of this Privacy Policy. If you do not agree, please discontinue use of our services.
        </p>
        <p>
          This Policy is compliant with the Digital Personal Data Protection Act, 2023 (DPDP Act), the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (SPDI Rules), and incorporates globally accepted best practices including GDPR-aligned principles.
        </p>
        <Callout>
          Why this matters to you: We built this policy to be transparent and honest with you. Our mission is to help your business grow, and earning your trust is the foundation of that relationship. Your data is safe with us.
        </Callout>

        {/* 2 ─ Information We Collect */}
        <SectionHeading n="2" title="Information We Collect" />
        <p>
          We collect information that you provide directly, information generated through your use of our services, and information from third-party sources. Below is a detailed breakdown:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">2.1 Personal Information</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Full name, gender, and date of birth</li>
          <li>Email address and mobile/phone number</li>
          <li>Residential or business address, city, state, and PIN code</li>
          <li>PAN card, Aadhaar number, or other government-issued ID (only when required for DPIIT registration, loan applications, or government scheme facilitation)</li>
          <li>Professional designation, industry, and years of experience</li>
          <li>WhatsApp number and communication preferences</li>
          <li>Photographs or profile pictures (if submitted voluntarily)</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">2.2 Business Information</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Business name, type of entity (LLP, Pvt Ltd, Proprietorship, etc.), and registration number</li>
          <li>GST number, MSME registration number, CIN, or DPIIT recognition certificate details</li>
          <li>Annual turnover, projected revenue, and financial statements (when required for loan/subsidy facilitation)</li>
          <li>Bank account details (only when required for government scheme disbursement assistance)</li>
          <li>Business plan, pitch deck, or funding requirements</li>
          <li>Details of existing liabilities, credit score, or CIBIL information (with your explicit consent)</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">2.3 Technical Data</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>IP address and approximate geographic location derived from IP</li>
          <li>Browser type, version, and language settings</li>
          <li>Operating system and device type (mobile, tablet, desktop)</li>
          <li>Screen resolution and hardware configuration</li>
          <li>Referring URL and exit pages</li>
          <li>Session duration, pages visited, and interaction patterns</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">2.4 Usage Data</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Forms filled and services enquired about</li>
          <li>Consultation booking history and appointment preferences</li>
          <li>Engagement with marketing emails, SMS, and WhatsApp messages (open rates, click rates)</li>
          <li>Survey responses and feedback submitted</li>
          <li>Chat logs and customer support interactions</li>
          <li>Interaction with AI-powered tools or chatbots (applicable to future services)</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">2.5 Information Collected Automatically (Cookies &amp; Tracking)</h3>
        <p>
          When you visit our website or app, we automatically collect certain data through cookies, pixel tags, web beacons, and similar tracking technologies. Please refer to Section 4 (Cookies Policy) for detailed information.
        </p>
        <Callout>
          Why this matters to you: We only collect information that is genuinely necessary to serve you better and to comply with legal obligations. We never collect more than what is needed, and sensitive financial details are collected only with your explicit consent.
        </Callout>

        {/* 3 ─ How We Use Your Information */}
        <SectionHeading n="3" title="How We Use Your Information" />
        <p>
          We use your information for the following clearly defined purposes. We process your data only on a lawful basis as defined under the DPDP Act 2023 and applicable law:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.1 Service Delivery</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Providing startup consultancy, DPIIT registration, and compliance support</li>
          <li>Processing applications for government schemes such as Mudra, PMEGP, Startup India, and other subsidies</li>
          <li>Facilitating loan enquiries and matching you with relevant lenders or financial institutions</li>
          <li>Delivering digital marketing and technology consulting services</li>
          <li>Scheduling, confirming, and conducting consultation sessions</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.2 Communication &amp; Relationship Management</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sending service updates, appointment reminders, and follow-up messages via email, SMS, or WhatsApp</li>
          <li>Responding to your queries, grievances, or support requests</li>
          <li>Sending newsletters, industry insights, and relevant government scheme updates (with your consent)</li>
          <li>Conducting satisfaction surveys and collecting feedback</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.3 Marketing &amp; Lead Generation</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Running targeted advertising campaigns on platforms including Google Ads, Meta (Facebook/Instagram), and LinkedIn</li>
          <li>Retargeting website visitors who have shown interest in our services</li>
          <li>Analysing the effectiveness of our marketing campaigns and optimising our communications</li>
          <li>Creating and managing lookalike audiences on advertising platforms using anonymised data</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.4 Legal, Regulatory &amp; Safety Compliance</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Complying with applicable Indian laws, regulations, and court orders</li>
          <li>Preventing fraud, identity theft, or other illegal activities</li>
          <li>Verifying identity for government scheme applications or financial advisory services</li>
          <li>Maintaining audit trails and records as required by law</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.5 Platform Improvement &amp; Analytics</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Analysing website and app usage patterns to improve user experience</li>
          <li>Conducting internal research and business intelligence activities</li>
          <li>Training and improving future AI-powered features and recommendation engines (with appropriate anonymisation or consent)</li>
          <li>Detecting and resolving technical issues</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">3.6 WhatsApp &amp; Phone Communication Consent</h3>
        <p>
          By providing your mobile number and engaging with our services, you expressly consent to receive communications from Farsight Solutions LLP via WhatsApp messages, phone calls (including from our representatives), and automated SMS. These communications may include service updates, appointment reminders, consultation follow-ups, and promotional offers. You may opt out at any time by replying STOP to any WhatsApp message or SMS.
        </p>
        <Callout>
          Why this matters to you: Your data is never used in ways that conflict with the purpose for which it was collected. If we plan to use it for a new purpose, we will inform you and seek your consent.
        </Callout>

        {/* 4 ─ Cookies Policy */}
        <SectionHeading n="4" title="Cookies Policy" />
        <p>
          Cookies are small text files placed on your browser or device when you visit our website. They help us provide you with a better experience and enable certain essential functions. We use the following categories of cookies:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">4.1 Types of Cookies We Use</h3>
        <div className="overflow-x-auto my-4">
          <table className="w-full border-collapse text-[0.7rem]">
            <thead>
              <tr className="border-b-2 border-[#0D1B3E] text-left">
                <th className="pb-2 pr-3 font-semibold text-[#0D1B3E]">Cookie Type</th>
                <th className="pb-2 pr-3 font-semibold text-[#0D1B3E]">Purpose</th>
                <th className="pb-2 font-semibold text-[#0D1B3E]">Can Be Disabled?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr><td className="py-2 pr-3 font-medium">Essential</td><td className="py-2 pr-3">Enable core website functions such as login, form submission, and session management.</td><td className="py-2">No (required)</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Analytics</td><td className="py-2 pr-3">Collect aggregated data on website traffic and user behaviour via Google Analytics 4.</td><td className="py-2">Yes</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Marketing</td><td className="py-2 pr-3">Used for ad retargeting via Meta Pixel, Google Ads Conversion Tracking, and LinkedIn Insight Tag.</td><td className="py-2">Yes</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Preference</td><td className="py-2 pr-3">Remember your language, location, and form preferences to personalise your experience.</td><td className="py-2">Yes</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Third-Party</td><td className="py-2 pr-3">Set by embedded tools such as WhatsApp Chat Widget, CRM tools, or video content providers.</td><td className="py-2">Yes</td></tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">4.2 Managing Your Cookie Preferences</h3>
        <p>
          When you first visit our website, a cookie consent banner will appear. You can choose to accept all cookies, accept only essential cookies, or customise your preferences. You can change your preferences at any time by clicking the &ldquo;Cookie Settings&rdquo; link in the footer of our website.
        </p>
        <p>
          You can also control cookies at the browser level. Most browsers allow you to refuse all cookies or alert you when cookies are being sent. Please note that disabling certain cookies may affect the functionality of our website.
        </p>
        <Callout>
          Why this matters to you: We believe you should be in control of your online experience. Our cookie consent tool puts you in the driver&apos;s seat, letting you decide exactly what tracking you are comfortable with.
        </Callout>

        {/* 5 ─ Data Sharing & Disclosure */}
        <SectionHeading n="5" title="Data Sharing & Disclosure" />
        <p>
          We do not sell, rent, or trade your personal data to any third party for their independent marketing purposes. We are committed to this as a core business principle. However, in order to provide our services, we may share your data with the following categories of partners under strict confidentiality and data processing agreements:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">5.1 Service Providers &amp; Technology Partners</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Google LLC (Google Analytics 4, Google Ads, Google Workspace) — for website analytics, advertising, and email communication</li>
          <li>Meta Platforms Inc. (Meta Pixel, Facebook/Instagram Ads) — for marketing campaign management and ad retargeting</li>
          <li>Zoho Corporation / HubSpot / CRM platforms — for customer relationship management and lead tracking</li>
          <li>Twilio / MSG91 / similar providers — for SMS and WhatsApp communication services</li>
          <li>Razorpay / PayU or equivalent — for secure payment processing (where applicable)</li>
          <li>AWS (Amazon Web Services) / Google Cloud / other cloud providers — for data hosting and storage</li>
          <li>LinkedIn Corporation — for B2B marketing and outreach campaigns</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">5.2 Government Bodies &amp; Regulatory Authorities</h3>
        <p>
          We may share your information with government bodies including DPIIT, Ministry of MSME, SIDBI, NABARD, banks, or other regulatory authorities solely for the purpose of facilitating registrations, scheme applications, or compliance filings on your behalf, and only with your explicit authorisation.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">5.3 Legal &amp; Safety Disclosures</h3>
        <p>
          We may disclose your information if required by law, court order, or a lawful government request, or when we believe in good faith that such disclosure is necessary to protect our rights, prevent fraud, protect the safety of users, or investigate illegal activities.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">5.4 Business Transfers</h3>
        <p>
          In the event of a merger, acquisition, or sale of all or part of Farsight Solutions LLP&apos;s business assets, your personal data may be transferred to the acquiring entity. We will notify you via email and/or a prominent notice on our website if such a transfer occurs and your rights under this policy will continue to apply.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">5.5 Client Data Confidentiality</h3>
        <p>
          Farsight Solutions LLP treats all client data — including business plans, financial projections, funding information, and proprietary business details — with the highest degree of confidentiality. Our team members and any engaged consultants are bound by strict Non-Disclosure Agreements (NDAs) and internal data access controls. Client data is never shared with competitors or used for any purpose beyond the agreed scope of services.
        </p>
        <Callout>
          Why this matters to you: We share your data only when it is essential for delivering your services or complying with legal requirements. Every third-party partner we work with is vetted for data security compliance.
        </Callout>

        {/* 6 ─ Data Security Measures */}
        <SectionHeading n="6" title="Data Security Measures" />
        <p>
          The security of your personal and business information is our highest priority. We implement comprehensive technical, organisational, and administrative safeguards as per the standards prescribed under the IT Act 2000 and its associated rules:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">6.1 Technical Safeguards</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>256-bit SSL/TLS encryption for all data transmitted between your browser and our servers</li>
          <li>AES-256 encryption for data at rest on our servers and cloud storage</li>
          <li>Secure HTTPS protocol across all pages of our website and application</li>
          <li>Regular penetration testing and vulnerability assessments by qualified cybersecurity professionals</li>
          <li>Multi-factor authentication (MFA) for all internal system access by our team</li>
          <li>Automated intrusion detection and security monitoring systems</li>
          <li>Regular automated backups with tested disaster recovery procedures</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">6.2 Organisational Safeguards</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Role-based access control (RBAC) ensuring staff can only access data relevant to their job function</li>
          <li>Mandatory data protection and security training for all employees and consultants</li>
          <li>Signed Non-Disclosure Agreements (NDAs) with all team members and external partners</li>
          <li>Internal data handling policies aligned with the DPDP Act 2023</li>
          <li>Designated Data Protection Officer responsible for overseeing compliance</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">6.3 Limitations of Security</h3>
        <p>
          While we take every reasonable measure to protect your data, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but commit to notifying you promptly in the event of any data breach that affects your information (see Section 12: Data Breach Notification).
        </p>
        <Callout>
          Why this matters to you: We invest heavily in security infrastructure. Your financial and business details are treated with the same level of protection that a bank applies to financial records.
        </Callout>

        {/* 7 ─ Data Retention Policy */}
        <SectionHeading n="7" title="Data Retention Policy" />
        <p>
          We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, to comply with legal obligations, or to resolve disputes. The following general retention guidelines apply:
        </p>
        <div className="overflow-x-auto my-4">
          <table className="w-full border-collapse text-[0.7rem]">
            <thead>
              <tr className="border-b-2 border-[#0D1B3E] text-left">
                <th className="pb-2 pr-3 font-semibold text-[#0D1B3E]">Data Category</th>
                <th className="pb-2 pr-3 font-semibold text-[#0D1B3E]">Retention Period</th>
                <th className="pb-2 font-semibold text-[#0D1B3E]">Basis</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr><td className="py-2 pr-3">Lead &amp; enquiry data</td><td className="py-2 pr-3">3 years from last contact</td><td className="py-2">Business necessity &amp; legal compliance</td></tr>
              <tr><td className="py-2 pr-3">Active client data</td><td className="py-2 pr-3">Duration of engagement + 7 years</td><td className="py-2">Contractual &amp; legal obligation</td></tr>
              <tr><td className="py-2 pr-3">Financial &amp; tax-related documents</td><td className="py-2 pr-3">8 years</td><td className="py-2">Income Tax Act, GST law</td></tr>
              <tr><td className="py-2 pr-3">Government scheme application data</td><td className="py-2 pr-3">As required by scheme authority</td><td className="py-2">Regulatory compliance</td></tr>
              <tr><td className="py-2 pr-3">Marketing communication records</td><td className="py-2 pr-3">3 years or until opt-out</td><td className="py-2">Consent-based processing</td></tr>
              <tr><td className="py-2 pr-3">Website analytics &amp; usage data</td><td className="py-2 pr-3">26 months</td><td className="py-2">Service improvement</td></tr>
              <tr><td className="py-2 pr-3">Customer support records</td><td className="py-2 pr-3">3 years from resolution</td><td className="py-2">Quality assurance &amp; dispute resolution</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          Upon expiry of the applicable retention period, we will securely delete or anonymise your data so that it can no longer be associated with you. If you request deletion of your data before the retention period expires, we will comply where not legally required to retain it.
        </p>
        <Callout>
          Why this matters to you: We don&apos;t hold onto your data indefinitely. Clear timelines mean your information is not kept longer than it serves a legitimate purpose.
        </Callout>

        {/* 8 ─ Your Rights as a Data Principal */}
        <SectionHeading n="8" title="Your Rights as a Data Principal" />
        <p>
          Under the Digital Personal Data Protection Act, 2023 and applicable law, you have the following rights with respect to your personal data. We are committed to facilitating these rights promptly and without undue barriers:
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.1 Right to Access</h3>
        <p>
          You have the right to request a summary of what personal data we hold about you, the purposes for which it is processed, and the categories of persons with whom it has been shared. To exercise this right, submit a request to <a href="mailto:privacy@farsightsolutions.in" className="text-[#1565C0] hover:underline">privacy@farsightsolutions.in</a> with the subject line &ldquo;Data Access Request&rdquo;.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.2 Right to Correction</h3>
        <p>
          You have the right to request correction or update of any inaccurate or incomplete personal data we hold about you. We will endeavour to process such requests within 30 days.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.3 Right to Erasure (Right to be Forgotten)</h3>
        <p>
          You may request deletion of your personal data where it is no longer necessary for the purposes collected, you have withdrawn consent, or we have no lawful basis to retain it. Please note that we may need to retain certain information to comply with legal obligations even after receiving a deletion request.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.4 Right to Withdraw Consent</h3>
        <p>
          Where data processing is based on your consent, you have the right to withdraw that consent at any time. Withdrawal of consent does not affect the lawfulness of processing carried out before the withdrawal. To withdraw consent, email <a href="mailto:privacy@farsightsolutions.in" className="text-[#1565C0] hover:underline">privacy@farsightsolutions.in</a> or use the opt-out mechanisms specified in this Policy.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.5 Right to Grievance Redressal</h3>
        <p>
          If you believe your rights have been violated, you may raise a grievance with our Grievance Officer (see Section 12) or with the Data Protection Board of India, once constituted under the DPDP Act 2023.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.6 Right to Nominate</h3>
        <p>
          As per the DPDP Act 2023, you have the right to nominate any individual who shall exercise your data rights in the event of your death or incapacity.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">8.7 Opt-Out from Marketing Communications</h3>
        <p>You can opt out of marketing communications at any time using the following methods:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Email:</strong> Click the &ldquo;Unsubscribe&rdquo; link at the bottom of any marketing email</li>
          <li><strong>SMS/WhatsApp:</strong> Reply STOP to any promotional message</li>
          <li><strong>Phone:</strong> Call <a href="tel:+919821605930" className="text-[#1565C0] hover:underline">+91 9821605930</a> and request removal from our calling list</li>
          <li><strong>Email:</strong> Send an opt-out request to <a href="mailto:marketing@farsightsolutions.in" className="text-[#1565C0] hover:underline">marketing@farsightsolutions.in</a></li>
        </ul>
        <p>
          Please allow up to 7 business days for opt-out requests to take effect across all channels. Note that even after opting out of marketing communications, we may still send you transactional and service-related communications necessary for your active engagements with us.
        </p>
        <Callout>
          Why this matters to you: These are your fundamental rights under Indian law. We have made it easy for you to exercise them without any charges, delays, or unnecessary documentation.
        </Callout>

        {/* 9 ─ Third-Party Links */}
        <SectionHeading n="9" title="Third-Party Links Disclaimer" />
        <p>
          Our website, emails, and digital assets may contain links to third-party websites, social media platforms, government portals (such as Startup India, MSME Udyam, DPIIT, SIDBI websites), or embedded content from external services. These links are provided for your convenience and reference only.
        </p>
        <p>
          Farsight Solutions LLP has no control over the content, privacy policies, or practices of any third-party websites. We strongly recommend that you review the privacy policy of every third-party website you visit. We are not responsible for the privacy practices or the content of such external sites. Clicking on third-party links is done at your own risk.
        </p>

        {/* 10 ─ Children's Privacy */}
        <SectionHeading n="10" title="Children's Privacy" />
        <p>
          Our services are intended for adults, specifically entrepreneurs, business owners, professionals, and SME operators. Our website and services are not directed at, and are not intended for use by, individuals under the age of 18 years.
        </p>
        <p>
          We do not knowingly collect, process, or store personal data of individuals below 18 years of age. If we become aware that we have inadvertently collected personal data from a minor, we will take immediate steps to delete such data from our systems. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <a href="mailto:privacy@farsightsolutions.in" className="text-[#1565C0] hover:underline">privacy@farsightsolutions.in</a>.
        </p>

        {/* 11 ─ Updates to This Privacy Policy */}
        <SectionHeading n="11" title="Updates to This Privacy Policy" />
        <p>
          We reserve the right to update, modify, or revise this Privacy Policy at any time to reflect changes in our business practices, applicable laws, or technology. The updated version will be effective from the date specified at the top of the policy.
        </p>
        <p>
          In the event of material changes to this Privacy Policy, we will notify you through one or more of the following methods: a prominent notice on our website homepage, an email notification to your registered email address, or an in-app notification (for future app users). We encourage you to review this Privacy Policy periodically to stay informed. Your continued use of our services after the effective date of any changes constitutes your acceptance of the revised Policy.
        </p>
        <p>Previous versions of this Privacy Policy will be archived and available on request.</p>

        {/* 12 ─ Data Breach Notification */}
        <SectionHeading n="12" title="Data Breach Notification" />
        <p>
          In the unfortunate event of a personal data breach that is likely to result in a risk to your rights and freedoms, Farsight Solutions LLP is committed to the following response protocol, in compliance with the DPDP Act 2023 and IT Act 2000:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>We will conduct an immediate internal investigation to assess the nature, scope, and impact of the breach upon becoming aware of it.</li>
          <li>We will notify the Data Protection Board of India (once operational) of any breach that meets the statutory threshold, within the timeframes prescribed by law.</li>
          <li>We will notify affected data principals (i.e., you) without undue delay, and in any case within 72 hours of confirming the breach, unless the breach is unlikely to result in any risk to your rights.</li>
          <li>Our breach notification to you will include: the nature of the breach, the categories and approximate volume of data affected, likely consequences of the breach, measures taken or proposed to address the breach, and contact details of the Grievance Officer.</li>
          <li>We will maintain a detailed internal register of all data breaches, regardless of whether they require external notification.</li>
          <li>Following any breach, we will conduct a root cause analysis and implement corrective measures to prevent recurrence.</li>
        </ul>
        <p>
          If you suspect any unauthorised use of your account or data, please contact our Grievance Officer immediately at <a href="mailto:grievance@farsightsolutions.in" className="text-[#1565C0] hover:underline">grievance@farsightsolutions.in</a> or call <a href="tel:+919821605930" className="text-[#1565C0] hover:underline">+91 9821605930</a>.
        </p>

        {/* 13 ─ Consent */}
        <SectionHeading n="13" title="Consent" />
        <p>By using our website, submitting any lead generation form, consultation booking form, or any other digital or physical form associated with Farsight Solutions LLP, you:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Confirm that you have read and understood this Privacy Policy in its entirety</li>
          <li>Consent to the collection, processing, use, storage, and sharing of your personal data as described in this Policy</li>
          <li>Consent to receive communications from Farsight Solutions LLP via email, SMS, WhatsApp, and phone calls for service, transactional, and marketing purposes</li>
          <li>Acknowledge that your consent is voluntary, informed, and given without coercion</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">14.1 Basis for Processing</h3>
        <p>We process your data on the following lawful bases under the DPDP Act 2023:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Consent:</strong> Where you have provided explicit consent for a specific purpose</li>
          <li><strong>Contractual necessity:</strong> Where processing is necessary to perform a service agreement with you</li>
          <li><strong>Legal obligation:</strong> Where processing is required to comply with applicable Indian laws</li>
          <li><strong>Legitimate interests:</strong> Where processing serves a legitimate business interest that does not override your fundamental rights</li>
        </ul>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">14.2 Withdrawal of Consent</h3>
        <p>
          You have the right to withdraw consent at any time for any purpose where consent is the lawful basis. Withdrawal of consent is prospective and does not affect the lawfulness of any processing carried out prior to withdrawal. To withdraw consent, contact <a href="mailto:privacy@farsightsolutions.in" className="text-[#1565C0] hover:underline">privacy@farsightsolutions.in</a>.
        </p>
        <Callout>
          Why this matters to you: Your consent is never presumed. Every consent-based processing activity is clearly identified, and we make it as easy to withdraw your consent as it was to give it.
        </Callout>

        {/* 15 ─ Cross-Border Data Transfer */}
        <SectionHeading n="15" title="Cross-Border Data Transfer" />
        <p>
          As a primarily India-focused business, the majority of your personal data is stored and processed within India. However, certain third-party service providers and technology platforms we use (including Google LLC, Meta Platforms Inc., and cloud infrastructure providers) may store or process data on servers located outside India.
        </p>
        <p>Where such international data transfers occur, we ensure the following safeguards are in place:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>We only transfer data to countries or entities that provide an adequate level of data protection as determined under applicable Indian law</li>
          <li>We rely on appropriate contractual clauses, including Standard Contractual Clauses (SCCs) where applicable, to ensure your data is protected during international transfers</li>
          <li>We review the data protection practices of all international vendors and partners before engaging with them</li>
          <li>Once the Central Government of India notifies specific countries or conditions for cross-border data transfer under the DPDP Act 2023, we will update our practices accordingly to remain compliant</li>
        </ul>
        <p>
          If you have concerns about how your data is handled in international transfers, please contact our Grievance Officer at <a href="mailto:grievance@farsightsolutions.in" className="text-[#1565C0] hover:underline">grievance@farsightsolutions.in</a>.
        </p>

        {/* 16 ─ Lead Generation, Consultation Booking & Marketing */}
        <SectionHeading n="16" title="Lead Generation, Consultation Booking & Marketing" />

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">16.1 Lead Generation Forms</h3>
        <p>
          When you submit a lead generation form on our website or any affiliated landing page, you are providing express consent for Farsight Solutions LLP to contact you for the purpose stated in that form. Your submitted data will be stored in our CRM system and used to initiate and maintain a business relationship with you. We may contact you via phone, email, or WhatsApp within a reasonable timeframe following your submission.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">16.2 Consultation Booking</h3>
        <p>
          When you book a consultation — whether a free initial assessment or a paid advisory session — we will use the information provided to prepare for and deliver that consultation. Session details, notes, and follow-up action items may be stored in our secure systems. All information shared during a consultation session is treated as strictly confidential.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">16.3 Email &amp; SMS Marketing</h3>
        <p>
          If you have opted in to our mailing list or submitted a form, you may receive our newsletter, government scheme updates, startup tips, and promotional communications. Each such email will include a clear and functional unsubscribe link. You can also unsubscribe at any time by emailing <a href="mailto:marketing@farsightsolutions.in" className="text-[#1565C0] hover:underline">marketing@farsightsolutions.in</a>.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">16.4 Ad Retargeting</h3>
        <p>
          We use cookie-based and pixel-based retargeting to show you relevant advertisements across platforms such as Google Display Network, Facebook, Instagram, and LinkedIn after you have visited our website. You can opt out of retargeting by adjusting your cookie preferences on our site, or by using the ad settings provided by Google and Meta.
        </p>

        <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">16.5 No Sale of Personal Data</h3>
        <p>
          Farsight Solutions LLP does not, and will never, sell, rent, auction, or otherwise commercially transfer your personal data to any third party for their own marketing or commercial use. This is a firm and unconditional commitment.
        </p>

        {/* 17 ─ Governing Law & Dispute Resolution */}
        <SectionHeading n="17" title="Governing Law & Dispute Resolution" />
        <p>
          This Privacy Policy is governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Privacy Policy shall be subject to the exclusive jurisdiction of the courts located in Noida, Uttar Pradesh, India.
        </p>
        <p>
          Before initiating any legal proceedings, we encourage you to first raise your concern with our Grievance Officer who will make every effort to resolve the matter amicably within 30 days.
        </p>

        {/* ── Contact block ── */}
        <div className="mt-12 rounded-lg border border-gray-200 bg-[#F8F9FA] px-6 py-5 text-center">
          <p className="text-[0.7rem] font-bold uppercase tracking-[0.15em] text-[#C95A0E]">
            Questions About This Policy?
          </p>
          <p className="mt-2 text-[0.75rem] text-[#444]">
            We are always happy to explain how we handle your data.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-[0.72rem]">
            <a href="mailto:privacy@farsightsolutions.in" className="text-[#1565C0] hover:underline">
              privacy@farsightsolutions.in
            </a>
            <span className="text-gray-300">|</span>
            <a href="tel:+919821605930" className="text-[#1565C0] hover:underline">
              +91 9821605930
            </a>
            <span className="text-gray-300">|</span>
            <span className="text-[#444]">www.farsight.co.in</span>
          </div>
          <p className="mt-4 text-[0.6rem] text-gray-400">
            Farsight Solutions LLP &nbsp;|&nbsp; Privacy Policy &nbsp;|&nbsp; Version 3.5 &nbsp;|&nbsp; Effective: December 30, 2024<br />
            Compliant with: DPDP Act 2023 | IT Act 2000 | SPDI Rules 2011
          </p>
        </div>
      </article>
    </main>
  );
}
